import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Send } from "lucide-react";
import PatientAppLayout from "../../components/PatientAppLayout/PatientAppLayout";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { botReply, wantsCarePhoneIntent, wantsEscalation } from "../../patientSupport/botReply";
import { simulatedAdminReply } from "../../patientSupport/simulatedAdminReply";
import {
  connectPatientSupportChannel,
  emitPatientSupportMessage,
  emitPatientSupportRead,
  emitPatientSupportTyping,
  hasDedicatedSupportEnv,
} from "../../patientSupport/patientSupportSocket";
import ChatDeliveryTicks from "../../components/ChatDeliveryTicks/ChatDeliveryTicks";
import "./PatientSupportPage.scss";
import { formatWaChatTime } from "../../utils/formatWaChatTime";

function rid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function MessageBody({ text, dialPhone }) {
  const p = String(dialPhone || "").trim();
  const s = String(text ?? "");
  if (!p || !s.includes(p)) {
    return <span className="patient-support-page__bubble-plain">{s}</span>;
  }
  const i = s.indexOf(p);
  const tel = p.replace(/[^\d+]/g, "");
  return (
    <span className="patient-support-page__bubble-plain">
      {s.slice(0, i)}
      {tel ? (
        <a href={`tel:${tel}`} className="patient-support-page__telLink">
          {p}
        </a>
      ) : (
        p
      )}
      {s.slice(i + p.length)}
    </span>
  );
}

export default function PatientSupportPage() {
  const location = useLocation();
  const [phase, setPhase] = useState("bot");
  const [handoffUnlocked, setHandoffUnlocked] = useState(false);
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState("");
  const [socketStatus, setSocketStatus] = useState("");
  const [adminTyping, setAdminTyping] = useState(false);
  /** When set, care team has opened the thread (read receipts for your messages). */
  const [teamReadAt, setTeamReadAt] = useState("");
  const bottomRef = useRef(null);
  const typingEmitTimerRef = useRef(null);
  const typingClearTimerRef = useRef(null);
  const socketRef = useRef(null);
  const socketCleanupRef = useRef(null);
  const seenIds = useRef(new Set());
  const simTimers = useRef([]);
  const [patientCarePhone, setPatientCarePhone] = useState("");

  const append = useCallback((row) => {
    setRows((prev) => [...prev, row]);
  }, []);

  useEffect(() => {
    setPhase("bot");
    setHandoffUnlocked(false);
    const pre =
      typeof location.state?.prefillDraft === "string" ? String(location.state.prefillDraft).trim() : "";
    setDraft(pre);
    setSocketStatus("");
    seenIds.current.clear();
    simTimers.current.forEach((t) => window.clearTimeout(t));
    simTimers.current = [];
    setTeamReadAt("");
    setRows([
      {
        id: rid(),
        role: "bot",
        text: "Hi — ask about documents, bids, booking, or the app. Ask for our care team phone number anytime, or tap “Chat with a care team member” when it appears.",
        at: new Date().toISOString(),
      },
    ]);
  }, [location.key, location.state?.prefillDraft]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest({ method: "GET", url: ENDPOINTS.PLATFORM_SETTINGS_PUBLIC });
        const cfg = res?.data != null && typeof res.data === "object" ? res.data : res;
        const phone = String(cfg?.support?.patientCarePhone || "").trim();
        if (!cancelled) setPatientCarePhone(phone);
      } catch {
        if (!cancelled) setPatientCarePhone("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [rows]);

  useLayoutEffect(() => {
    if (phase !== "live") return undefined;

    const dedicated = hasDedicatedSupportEnv();
    setSocketStatus(dedicated ? "Connecting…" : "");

    const { socket, cleanup, mode } = connectPatientSupportChannel({
      onJoined: () => {
        setSocketStatus("Connected — a team member will reply when they pick this up.");
      },
      onMessage: (msg) => {
        if (!msg?.id || seenIds.current.has(msg.id)) return;
        seenIds.current.add(msg.id);
        const role = msg.from === "admin" ? "admin" : "user";
        append({
          id: msg.id,
          role,
          text: msg.text,
          at: msg.createdAt || new Date().toISOString(),
        });
      },
      onAdminRead: (payload) => {
        const t = payload?.readAt;
        if (!t) return;
        const d = new Date(t);
        if (Number.isFinite(d.getTime())) setTeamReadAt(t);
      },
      onHistory: (payload) => {
        const list = payload?.messages;
        if (!Array.isArray(list) || list.length === 0) return;
        setRows((prev) => {
          const byId = new Map();
          for (const r of prev) byId.set(r.id, r);
          for (const m of list) {
            if (!m?.id || m.text == null) continue;
            seenIds.current.add(m.id);
            byId.set(m.id, {
              id: m.id,
              role: m.from === "admin" ? "admin" : "user",
              text: m.text,
              at: m.createdAt || new Date().toISOString(),
            });
          }
          return Array.from(byId.values()).sort(
            (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
          );
        });
      },
      onError: (m) => {
        setSocketStatus("");
        append({ id: rid(), role: "system", text: m, at: new Date().toISOString() });
      },
    });

    socketRef.current = socket;
    socketCleanupRef.current = cleanup;

    if (!socket) {
      setSocketStatus("Demo: replies simulate a care team member.");
      return () => {
        socketCleanupRef.current?.();
        socketCleanupRef.current = null;
        socketRef.current = null;
        simTimers.current.forEach((t) => window.clearTimeout(t));
        simTimers.current = [];
      };
    }

    const onConnect = () => setSocketStatus("Connected.");
    const onDisconnect = () => setSocketStatus("Disconnected. Reconnecting…");
    const onSupportTyping = (payload) => {
      if (payload?.from !== "admin") return;
      setAdminTyping(!!payload?.isTyping);
      if (typingClearTimerRef.current) window.clearTimeout(typingClearTimerRef.current);
      if (payload?.isTyping) {
        typingClearTimerRef.current = window.setTimeout(() => setAdminTyping(false), 4500);
      }
    };
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("support:typing", onSupportTyping);
    if (mode === "main" && !dedicated) {
      setSocketStatus(socket.connected ? "Connected." : "Connecting…");
    }

    return () => {
      try {
        socket.off("connect", onConnect);
        socket.off("disconnect", onDisconnect);
        socket.off("support:typing", onSupportTyping);
      } catch (_) {}
      socketCleanupRef.current?.();
      socketCleanupRef.current = null;
      socketRef.current = null;
      setSocketStatus("");
      setAdminTyping(false);
      setTeamReadAt("");
      simTimers.current.forEach((t) => window.clearTimeout(t));
      simTimers.current = [];
      if (typingEmitTimerRef.current) window.clearTimeout(typingEmitTimerRef.current);
      if (typingClearTimerRef.current) window.clearTimeout(typingClearTimerRef.current);
    };
  }, [phase, append]);

  useEffect(() => {
    if (phase !== "live") return undefined;
    const s = socketRef.current;
    if (!s?.emit) return undefined;

    if (typingEmitTimerRef.current) window.clearTimeout(typingEmitTimerRef.current);
    typingEmitTimerRef.current = window.setTimeout(() => {
      try {
        emitPatientSupportTyping(s, !!draft.trim());
      } catch (_) {}
    }, 280);

    return () => {
      if (typingEmitTimerRef.current) window.clearTimeout(typingEmitTimerRef.current);
    };
  }, [draft, phase, socketStatus]);

  const patientReadDebounceRef = useRef(null);
  useEffect(() => {
    if (phase !== "live") return undefined;
    const s = socketRef.current;
    if (!s?.emit) return undefined;
    const lastAdmin = [...rows].reverse().find((r) => r.role === "admin");
    if (!lastAdmin?.id) return undefined;
    if (patientReadDebounceRef.current) window.clearTimeout(patientReadDebounceRef.current);
    patientReadDebounceRef.current = window.setTimeout(() => {
      emitPatientSupportRead(s, lastAdmin.id);
    }, 700);
    return () => {
      if (patientReadDebounceRef.current) window.clearTimeout(patientReadDebounceRef.current);
    };
  }, [phase, rows]);

  useEffect(() => {
    return () => {
      if (phase !== "live") return;
      const s = socketRef.current;
      if (s?.emit) {
        try {
          emitPatientSupportTyping(s, false);
        } catch (_) {}
      }
    };
  }, [phase]);

  const sendBot = (text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed) return;
    append({ id: rid(), role: "user", text: trimmed, at: new Date().toISOString() });
    setDraft("");
    if (wantsEscalation(trimmed) && !wantsCarePhoneIntent(trimmed)) {
      setHandoffUnlocked(true);
      append({
        id: rid(),
        role: "bot",
        text: "Happy to connect you with the team. Tap “Chat with a care team member” when you are ready.",
        at: new Date().toISOString(),
      });
      return;
    }
    const t = window.setTimeout(() => {
      append({
        id: rid(),
        role: "bot",
        text: botReply(trimmed, { patientCarePhone }),
        at: new Date().toISOString(),
      });
      setHandoffUnlocked(true);
    }, 450);
    simTimers.current.push(t);
  };

  const sendLive = (text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed) return;
    setDraft("");

    const dedicated = hasDedicatedSupportEnv();
    const s = socketRef.current;

    const emitToServerOrQueue = (sock) => {
      if (!sock?.emit) return false;
      const run = () => {
        try {
          emitPatientSupportMessage(sock, trimmed);
        } catch (_) {}
      };
      if (sock.connected) {
        run();
        return true;
      }
      let warnTimer;
      const onConnect = () => {
        try {
          window.clearTimeout(warnTimer);
        } catch (_) {}
        try {
          sock.off("connect", onConnect);
        } catch (_) {}
        run();
      };
      try {
        sock.on("connect", onConnect);
      } catch (_) {
        return false;
      }
      warnTimer = window.setTimeout(() => {
        try {
          sock.off("connect", onConnect);
        } catch (_) {}
        if (!sock.connected) {
          append({
            id: rid(),
            role: "system",
            text: "Could not reach support. Check your connection and try sending again.",
            at: new Date().toISOString(),
          });
        }
      }, 15000);
      if (sock.connected) {
        try {
          window.clearTimeout(warnTimer);
        } catch (_) {}
        try {
          sock.off("connect", onConnect);
        } catch (_) {}
        run();
      }
      return true;
    };

    if (dedicated) {
      if (s?.connected) {
        emitPatientSupportMessage(s, trimmed);
        return;
      }
      if (s && emitToServerOrQueue(s)) {
        return;
      }
      append({
        id: rid(),
        role: "system",
        text: "Still connecting to support — wait a moment and try again.",
        at: new Date().toISOString(),
      });
      return;
    }

    if (s?.connected) {
      emitPatientSupportMessage(s, trimmed);
      return;
    }
    if (s && emitToServerOrQueue(s)) {
      return;
    }

    append({ id: rid(), role: "user", text: trimmed, at: new Date().toISOString() });
    const t = window.setTimeout(() => {
      append({
        id: rid(),
        role: "admin",
        text: simulatedAdminReply(trimmed),
        at: new Date().toISOString(),
      });
    }, 900 + Math.floor(Math.random() * 700));
    simTimers.current.push(t);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (phase === "bot") sendBot(draft);
    else sendLive(draft);
  };

  const goLive = () => {
    setPhase("live");
    append({
      id: rid(),
      role: "system",
      text: "Handoff to care team — chat in real time when an admin is online.",
      at: new Date().toISOString(),
    });
  };

  return (
    <PatientAppLayout toolbarTitle={phase === "bot" ? "Help assistant" : "Care team chat"}>
      <div className="patient-support-page">
        <header className="patient-support-page__head patient-support-page__head--afterGlobalBar">
          {phase === "live" ? (
            <p className="patient-support-page__lead">
              Live chat with HealBid admin — use Messages for doctors and booking updates.
            </p>
          ) : null}
          {phase === "live" && socketStatus ? (
            <p className="patient-support-page__status">{socketStatus}</p>
          ) : null}
          {phase === "live" && teamReadAt ? (
            <p className="patient-support-page__seen">Care team viewed this chat.</p>
          ) : null}
          {phase === "live" && adminTyping ? (
            <p className="patient-support-page__typing">Care team is typing…</p>
          ) : null}
        </header>

        <div className="patient-support-page__stream">
          {rows.map((r) => {
            const timeStr = formatWaChatTime(r.at);
            const readByTeam =
              phase === "live" &&
              r.role === "user" &&
              teamReadAt &&
              Number.isFinite(new Date(r.at).getTime()) &&
              Number.isFinite(new Date(teamReadAt).getTime()) &&
              new Date(r.at).getTime() <= new Date(teamReadAt).getTime() + 2000;
            return (
              <div
                key={r.id}
                className={`patient-support-page__bubble patient-support-page__bubble--${r.role}`}
              >
                {r.role === "admin" ? (
                  <div className="patient-support-page__bubble-label">Care team</div>
                ) : null}
                {r.role === "bot" ? (
                  <div className="patient-support-page__bubble-label">Assistant</div>
                ) : null}
                {r.role === "system" ? (
                  <div className="patient-support-page__bubble-text">{r.text}</div>
                ) : (
                  <div className="patient-support-page__bubble-body">
                    <div className="patient-support-page__bubble-text">
                      {r.role === "user" ? (
                        r.text
                      ) : (
                        <MessageBody text={r.text} dialPhone={patientCarePhone} />
                      )}
                    </div>
                    <div className="patient-support-page__bubble-meta">
                      <span className="patient-support-page__bubble-time">{timeStr}</span>
                      {phase === "live" && r.role === "user" ? (
                        <ChatDeliveryTicks status={readByTeam ? "read" : "delivered"} theme="onTeal" />
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {phase === "bot" && handoffUnlocked ? (
          <div className="patient-support-page__handoff">
            <p className="patient-support-page__handoff-note">
              You will chat directly with an admin on the care team.
            </p>
            <button type="button" className="patient-support-page__handoff-btn" onClick={goLive}>
              Chat with a care team member
            </button>
          </div>
        ) : null}

        <form className="patient-support-page__composer" onSubmit={onSubmit}>
          <input
            className="patient-support-page__input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={phase === "bot" ? "Ask the assistant…" : "Message the team…"}
            autoComplete="off"
          />
          <button type="submit" className="patient-support-page__send" disabled={!draft.trim()} aria-label="Send">
            <Send size={18} strokeWidth={2.2} />
          </button>
        </form>
      </div>
    </PatientAppLayout>
  );
}
